import { useState, useRef, useCallback, useEffect } from "react"
import { Link } from "@inertiajs/react"
import MapGL, { Marker, Source, Layer } from "react-map-gl/mapbox"
import "mapbox-gl/dist/mapbox-gl.css"
import type { MapRef } from "react-map-gl/mapbox"
import { CommunePluLayer } from "@/components/explore/layers/commune-plu-layer"
import { IsochroneLayer } from "@/components/explore/layers/isochrone-layer"
import { fetchPluDocument, getPLUPartitionAreas } from "@/lib/explore/api/plu"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Google3DMap } from "@/components/explore/google-3d-map"
import { StreetViewMap } from "@/components/explore/street-view-map"
import { MapPin, Compass, Map, Grid3x3, Satellite, Rotate3d, Video } from "lucide-react"
import type { Project } from "@/types"
import type { IParcel } from "@/types/explore"
import type { Feature, FeatureCollection } from "geojson"

const MAPBOX_STYLES = {
  street: "mapbox://styles/kaptcher/cm1umwib0015k01qr8tra14o9",
  cadastre: "mapbox://styles/mapbox/light-v11",
  satellite: "mapbox://styles/kaptcher/cltell1nn01dm01piblgud9d2",
} as const

const STYLE_ITEMS = [
  { id: "street", label: "Plan", icon: Map },
  { id: "cadastre", label: "Cadastre", icon: Grid3x3 },
  { id: "satellite", label: "Satellite", icon: Satellite },
  { id: "3d", label: "3D", icon: Rotate3d },
  { id: "streetview", label: "Street", icon: Video },
] as const

type StyleKey = (typeof STYLE_ITEMS)[number]["id"]

const CADASTRE_VECTOR_SOURCE = "https://openmaptiles.geo.data.gouv.fr/data/cadastre/{z}/{x}/{y}.pbf"

type Props = {
  project: Project
  mapboxToken: string
  googleMapsKey: string
  fetchedParcels: IParcel[]
  parcelGeojson: FeatureCollection | null
}

export function ProjectMap({ project, mapboxToken, googleMapsKey, fetchedParcels, parcelGeojson }: Props) {
  const { latitude, longitude } = project

  if (!latitude || !longitude) {
    return <NoLocationFallback project={project} />
  }

  return <ProjectMapInner project={project} mapboxToken={mapboxToken} googleMapsKey={googleMapsKey} fetchedParcels={fetchedParcels} parcelGeojson={parcelGeojson} lat={latitude} lng={longitude} />
}

function NoLocationFallback({ project }: { project: Project }) {
  const locationParts = [
    project.address,
    [project.city_name, project.zip_code].filter(Boolean).join(" "),
  ].filter(Boolean)

  return (
    <div
      className="relative flex aspect-square flex-col items-center justify-center gap-4 bg-muted/40 p-6"
      style={{
        backgroundImage: "radial-gradient(circle, hsl(var(--border) / 0.4) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
      }}
    >
      <div className="flex size-14 items-center justify-center rounded-full bg-background shadow-sm">
        <MapPin className="size-6 text-muted-foreground" />
      </div>
      <div className="text-center">
        {locationParts.length > 0 ? (
          locationParts.map((part, i) => (
            <p key={i} className={i === 0 ? "text-sm font-medium" : "text-sm text-muted-foreground"}>
              {part}
            </p>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Aucune adresse</p>
        )}
      </div>
    </div>
  )
}

function ProjectMapInner({
  project,
  mapboxToken,
  googleMapsKey,
  fetchedParcels,
  parcelGeojson,
  lat,
  lng,
}: {
  project: Project
  mapboxToken: string
  googleMapsKey: string
  fetchedParcels: IParcel[]
  parcelGeojson: FeatureCollection | null
  lat: number
  lng: number
}) {
  const mapRef = useRef<MapRef>(null)
  const [activeStyle, setActiveStyle] = useState<StyleKey>("street")
  const isMapboxStyle = activeStyle !== "3d" && activeStyle !== "streetview"
  const [showCadastre, setShowCadastre] = useState(false)
  const [showPlu, setShowPlu] = useState(false)
  const [pluData, setPluData] = useState<Feature[]>([])
  const [pluPartition, setPluPartition] = useState<string | null>(null)
  const [currentZoom, setCurrentZoom] = useState(16)
  const [isoWalk, setIsoWalk] = useState(false)
  const [isoBike, setIsoBike] = useState(false)
  const [isoDrive, setIsoDrive] = useState(false)

  const parcelColor = project.status === "asset" ? "#22c55e" : "#8b5cf6"
  const bgColor = project.pipe_status?.bg_color || "#6366f1"
  const textColor = project.pipe_status?.text_color || "#ffffff"

  // Fetch PLU data
  const fetchPluAtCenter = useCallback(async () => {
    const map = mapRef.current
    if (!map) return
    const zoom = map.getZoom()
    if (zoom < 10) {
      if (pluPartition) {
        setPluPartition(null)
        setPluData([])
      }
      return
    }

    const center = map.getCenter()
    const doc = await fetchPluDocument(center.lng, center.lat)
    if (!doc?.partition || doc.partition === pluPartition) return

    setPluPartition(doc.partition)
    try {
      const pluAreas = await getPLUPartitionAreas(doc.partition)
      setPluData(pluAreas.features as Feature[])
    } catch {
      setPluData([])
    }
  }, [pluPartition])

  const handleMoveEnd = useCallback(() => {
    const map = mapRef.current
    if (map) setCurrentZoom(map.getZoom())
    if (showPlu) fetchPluAtCenter()
  }, [showPlu, fetchPluAtCenter])

  useEffect(() => {
    if (showPlu) fetchPluAtCenter()
  }, [showPlu])

  const center: [number, number] = [lng, lat]

  return (
    <div className="relative aspect-square overflow-hidden [&_.mapboxgl-ctrl-logo]:!hidden">
      {activeStyle === "3d" ? (
        <Google3DMap
          longitude={lng}
          latitude={lat}
          googleMapsKey={googleMapsKey}
          selectedParcels={fetchedParcels}
        />
      ) : activeStyle === "streetview" ? (
        <StreetViewMap coordinates={[lng, lat]} googleMapsKey={googleMapsKey} />
      ) : (
        <MapGL
          ref={mapRef}
          mapboxAccessToken={mapboxToken}
          mapStyle={MAPBOX_STYLES[activeStyle]}
          initialViewState={{ longitude: lng, latitude: lat, zoom: 16 }}
          onMoveEnd={handleMoveEnd}
          style={{ width: "100%", height: "100%" }}
          attributionControl={false}
        >
          {/* Cadastre vector tiles */}
          {showCadastre && (
            <Source id="cadastre-vector" type="vector" tiles={[CADASTRE_VECTOR_SOURCE]} minzoom={11} maxzoom={16}>
              <Layer
                id="cadastre-parcels-line"
                source-layer="parcelles"
                type="line"
                paint={{ "line-color": "#000000", "line-width": 1, "line-opacity": 0.5 }}
              />
              <Layer
                id="cadastre-parcels-label"
                source-layer="parcelles"
                type="symbol"
                minzoom={16}
                filter={["!=", ["get", "section"], ""]}
                layout={{
                  "text-field": ["concat", ["get", "section"], " ", ["get", "numero"]],
                  "text-size": 14,
                  "text-allow-overlap": false,
                }}
                paint={{ "text-color": "#000000", "text-halo-color": "#ffffff", "text-halo-width": 1.5 }}
              />
            </Source>
          )}

          {/* PLU layer */}
          {showPlu && pluData.length > 0 && (
            <CommunePluLayer pluTypeZones={["U", "AUc", "AUs", "N", "A"]} pluData={pluData} opacity={0.4} />
          )}

          {/* Isochrone layers */}
          {isoWalk && <IsochroneLayer profile="walking" center={center} opacity={0.5} />}
          {isoBike && <IsochroneLayer profile="cycling" center={center} opacity={0.5} />}
          {isoDrive && <IsochroneLayer profile="driving" center={center} opacity={0.5} />}

          {/* Project parcels */}
          {parcelGeojson && (
            <Source id="project-parcels" type="geojson" data={parcelGeojson}>
              <Layer
                id="project-parcels-fill"
                type="fill"
                paint={{ "fill-color": parcelColor, "fill-opacity": 0.35 }}
              />
              <Layer
                id="project-parcels-outline"
                type="line"
                paint={{ "line-color": parcelColor, "line-width": 2 }}
              />
            </Source>
          )}

          {/* Project marker */}
          <Marker longitude={lng} latitude={lat} anchor="bottom">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill={bgColor}
              stroke={textColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </Marker>
        </MapGL>
      )}

      {/* Style selector */}
      <div className="absolute top-3 left-3 flex gap-0.5 rounded-lg bg-background/90 p-1 shadow-sm backdrop-blur-sm">
        {STYLE_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveStyle(id)}
            disabled={activeStyle === id}
            className={`flex flex-col items-center justify-center size-10 rounded-md transition-all text-xs ${
              activeStyle === id
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
            }`}
          >
            <Icon className="size-4" />
            <span className="text-[10px] leading-tight mt-0.5 font-medium">{label}</span>
          </button>
        ))}
      </div>

      {/* Layer toggles — hidden in 3D / Street View */}
      {isMapboxStyle && (
        <div className="absolute top-3 right-3 flex flex-col gap-2 rounded-lg border bg-background/90 p-3 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Switch id="pm-cadastre" checked={showCadastre} onCheckedChange={setShowCadastre} />
            <Label htmlFor="pm-cadastre" className="text-xs cursor-pointer">
              Cadastre
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="pm-plu" checked={showPlu} onCheckedChange={setShowPlu} disabled={currentZoom < 10} />
            <Label
              htmlFor="pm-plu"
              className={`text-xs ${currentZoom < 10 ? "text-muted-foreground/50" : "cursor-pointer"}`}
            >
              PLU{currentZoom < 10 ? " (zoomer +)" : ""}
            </Label>
          </div>
          <div className="my-0.5 h-px bg-border" />
          <div className="flex items-center gap-2">
            <Switch id="pm-iso-walk" checked={isoWalk} onCheckedChange={setIsoWalk} />
            <Label htmlFor="pm-iso-walk" className="text-xs cursor-pointer">
              Isochrone piéton
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="pm-iso-bike" checked={isoBike} onCheckedChange={setIsoBike} />
            <Label htmlFor="pm-iso-bike" className="text-xs cursor-pointer">
              Isochrone vélo
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="pm-iso-drive" checked={isoDrive} onCheckedChange={setIsoDrive} />
            <Label htmlFor="pm-iso-drive" className="text-xs cursor-pointer">
              Isochrone voiture
            </Label>
          </div>
        </div>
      )}

      {/* Explorer link */}
      <div className="absolute bottom-3 left-3">
        <Link
          href={`/explore?lat=${lat}&lng=${lng}${project.city_code ? `&commune=${project.city_code}` : ""}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1.5 text-xs font-medium text-primary shadow-sm backdrop-blur-sm hover:bg-background"
        >
          <Compass className="size-3.5" />
          Voir sur l'explorateur
        </Link>
      </div>
    </div>
  )
}
